import { tgpu, d } from 'typegpu';
import type { TgpuRoot, TgpuBuffer, TgpuGuardedComputePipeline, TgpuBindGroup } from 'typegpu';
import type { StorageFlag, UniformFlag } from 'typegpu';
import type { WgslArray } from 'typegpu/data';
import type { SimulationConfig, Resolution } from '../config/schema';
import { createSystemFromConfig } from '../systems/registry';
import { DataCell } from '../systems/shared/DataCell';
import { isNeighborVizMode } from '../systems/shared/neighbor';
import type { System } from '../systems/System';

const TilerParams = d.struct({
  canvasRes: d.f32,
  tileCol: d.f32,
  tileRow: d.f32,
  tileW: d.f32,
  tileH: d.f32,
  tileRes: d.f32,
  _pad: d.f32,
  _pad2: d.f32,
});

export class Tiler {
  private root: TgpuRoot;
  private config: SimulationConfig;
  private cols: number;
  private rows: number;
  private totalTiles: number;
  private tileW: number;
  private tileH: number;
  private tileRes: number;
  private tileCellCount: number;
  private canvasCellCount: number;
  private currentTile = 0;
  private tileSteps = 0;
  private tileDone = false;
  private compositeBuf: TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag;
  private copyPipeline: TgpuGuardedComputePipeline;
  private copyBG: TgpuBindGroup;
  private copyParamsBuf: TgpuBuffer<typeof TilerParams> & UniformFlag;
  private system: System;
  private isDivergence: boolean;
  private isNeighbor: boolean;

  constructor(root: TgpuRoot, config: SimulationConfig) {
    this.root = root;
    this.config = config;
    this.cols = config.phaseSpace.tiling.cols;
    this.rows = config.phaseSpace.tiling.rows;
    this.totalTiles = this.cols * this.rows;
    this.canvasCellCount = config.resolution * config.resolution;
    this.tileW = Math.floor(config.resolution / this.cols);
    this.tileH = Math.floor(config.resolution / this.rows);
    this.tileRes = Math.max(1, Math.min(this.tileW, this.tileH));
    this.tileCellCount = this.tileRes * this.tileRes;
    this.isDivergence = config.vizMode === 'divergence' || config.vizMode === 'divergenceDistance';
    this.isNeighbor = isNeighborVizMode(config.vizMode);

    const canvasDataArr = d.arrayOf(DataCell, this.canvasCellCount);
    this.compositeBuf = root.createBuffer(canvasDataArr,
      new Array(this.canvasCellCount).fill({ r: 0, g: 0, b: 0, a: 0 }),
    ).$usage('storage') as unknown as TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag;

    this.copyParamsBuf = root.createBuffer(TilerParams, {
      canvasRes: config.resolution, tileCol: 0, tileRow: 0,
      tileW: this.tileW, tileH: this.tileH, tileRes: this.tileRes, _pad: 0, _pad2: 0,
    }).$usage('uniform') as unknown as TgpuBuffer<typeof TilerParams> & UniformFlag;

    const tileDataArr = d.arrayOf(DataCell, this.tileCellCount);
    const copyLayout = tgpu.bindGroupLayout({
      src: { storage: tileDataArr },
      dst: { storage: canvasDataArr, access: 'mutable' },
      params: { uniform: TilerParams },
    });
    const cl = copyLayout;
    const tileCopy = (ci: number) => {
      'use gpu';
      const p = cl.$.params;
      const canvasRes = d.u32(p.canvasRes);
      const tileRes = d.u32(p.tileRes);
      const tw = p.tileW;
      const th = p.tileH;
      const sx = ci % tileRes;
      const sy = ci / tileRes;
      const dx = d.u32(p.tileCol * tw + d.f32(sx) * tw / d.f32(tileRes));
      const dy = d.u32(p.tileRow * th + d.f32(sy) * th / d.f32(tileRes));
      if (dx < canvasRes && dy < canvasRes) {
        cl.$.dst[dy * canvasRes + dx] = DataCell(cl.$.src[ci]);
      }
    };
    this.copyPipeline = root.createGuardedComputePipeline(tileCopy);

    const tileConfig = { ...config, resolution: this.tileRes as unknown as Resolution };
    this.system = createSystemFromConfig(config);
    this.system.build(root, tileConfig, this.tileCellCount);

    const srcBuf = this.system.getData(config.vizMode) as unknown as TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag;
    this.copyBG = root.createBindGroup(copyLayout, {
      src: srcBuf, dst: this.compositeBuf, params: this.copyParamsBuf,
    });

    this.startTile();
  }

  get data() { return this.compositeBuf; }

  getFrameCount(): number { return this.tileSteps; }

  isComplete(): boolean { return this.currentTile >= this.totalTiles; }

  getProgress(): { current: number; total: number } {
    return { current: this.currentTile + (this.tileDone ? 1 : 0), total: this.totalTiles };
  }

  step(): void {
    if (this.isComplete()) return;

    const batchSize = Math.min(this.config.iterationsPerFrame, this.config.maxIter - this.tileSteps);
    for (let i = 0; i < batchSize; i++) {
      if (this.isDivergence) {
        this.system.divergenceStep();
      } else if (this.isNeighbor) {
        this.system.neighborStep();
      } else {
        this.system.stepSim();
      }
      this.tileSteps++;
      if (this.tileSteps >= this.config.maxIter) break;
    }
    if (!this.isDivergence && !this.isNeighbor) {
      this.system.accumulateSim();
    }

    if (this.tileSteps >= this.config.maxIter) {
      this.compositeTile();
      this.currentTile++;
      if (this.currentTile < this.totalTiles) {
        this.tileSteps = 0;
        this.startTile();
      } else {
        this.tileDone = true;
      }
    }
  }

  private startTile(): void {
    const tileConfig = { ...this.config, resolution: this.tileRes as unknown as Resolution };
    (tileConfig as unknown as Record<string, number>)._tileCol = this.currentTile % this.cols;
    (tileConfig as unknown as Record<string, number>)._tileRow = Math.floor(this.currentTile / this.cols);
    this.system.updateParams(tileConfig);
    if (this.isDivergence) {
      this.system.initDivergence(this.config.seed, this.config.perturb);
    } else if (this.isNeighbor) {
      this.system.neighborInit(this.config.seed, this.config.perturb);
    } else {
      this.system.initSim();
    }
  }

  private compositeTile(): void {
    const col = this.currentTile % this.cols;
    const row = Math.floor(this.currentTile / this.cols);
    this.copyParamsBuf.write({
      canvasRes: this.config.resolution, tileCol: col, tileRow: row,
      tileW: this.tileW, tileH: this.tileH, tileRes: this.tileRes, _pad: 0, _pad2: 0,
    } as never);
    this.copyPipeline.with(this.copyBG).dispatchThreads(this.tileCellCount);
  }
}
